import React, { useEffect, useState, useMemo } from 'react';
import PageLayout from '../components/PageLayout';
import Button from '../components/Button';
import ConfirmDrawer from '../components/ConfirmDrawer';
import { listUsers, createUser, updateUser, deleteUser } from '../api/users';
import { useUser } from '../context/UserContext';
import toast from 'react-hot-toast';
import AdminSettingsPanel from '../components/AdminSettingsPanel';
import { Search, UserPlus, X, ChevronDown } from 'lucide-react';

const ROLES = ['secretaire', 'infirmiere', 'medecin', 'admin'];
const ROLE_LABELS = {
  secretaire: 'Secrétaire',
  infirmiere: 'Infirmière',
  medecin: 'Médecin',
  admin: 'Admin',
};
const ROLE_COLORS = {
  secretaire: 'bg-blue-100 text-blue-800',
  infirmiere: 'bg-green-100 text-green-800',
  medecin: 'bg-purple-100 text-purple-800',
  admin: 'bg-red-100 text-red-800',
};

const INPUT_CLS =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300';

function UserFormFields({ form, onChange, isEdit = false }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Identifiant *</label>
        <input
          className={INPUT_CLS}
          placeholder="ex: doc001"
          value={form.identifiant}
          onChange={(e) => onChange('identifiant', e.target.value)}
        />
      </div>
      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Email</label>
        <input
          className={INPUT_CLS}
          type="email"
          placeholder="optionnel"
          value={form.email}
          onChange={(e) => onChange('email', e.target.value)}
        />
      </div>
      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Nom</label>
        <input
          className={INPUT_CLS}
          placeholder="Nom de famille"
          value={form.nom}
          onChange={(e) => onChange('nom', e.target.value)}
        />
      </div>
      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Prénom</label>
        <input
          className={INPUT_CLS}
          placeholder="Prénom"
          value={form.prenom}
          onChange={(e) => onChange('prenom', e.target.value)}
        />
      </div>
      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Rôle</label>
        <select
          className={INPUT_CLS}
          value={form.role}
          onChange={(e) => onChange('role', e.target.value)}
        >
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r]}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">
          Mot de passe{' '}
          {isEdit && <span className="text-gray-400">(laisser vide = inchangé)</span>}
        </label>
        <input
          className={INPUT_CLS}
          type="password"
          placeholder={isEdit ? '••••••••' : 'Mot de passe *'}
          value={form.password}
          onChange={(e) => onChange('password', e.target.value)}
        />
      </div>
      <div className="sm:col-span-2">
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-red-600"
            checked={!!form.actif}
            onChange={(e) => onChange('actif', e.target.checked)}
          />
          <span>Compte actif</span>
        </label>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const { logout } = useUser();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Recherche & filtres
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterActif, setFilterActif] = useState('');

  // Modal création
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    identifiant: '',
    email: '',
    nom: '',
    prenom: '',
    role: 'secretaire',
    password: '',
    actif: true,
  });

  // Edition inline
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Suppression
  const [confirm, setConfirm] = useState(null);

  const refresh = async () => {
    setError('');
    setLoading(true);
    try {
      const data = await listUsers();
      setUsers(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  // Liste filtrée/recherchée
  const filtered = useMemo(() => {
    let list = users;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (u) =>
          (u.nom || '').toLowerCase().includes(q) ||
          (u.prenom || '').toLowerCase().includes(q) ||
          (u.identifiant || '').toLowerCase().includes(q) ||
          (u.email || '').toLowerCase().includes(q),
      );
    }
    if (filterRole) list = list.filter((u) => u.role === filterRole);
    if (filterActif !== '') list = list.filter((u) => String(u.actif) === filterActif);
    return list;
  }, [users, search, filterRole, filterActif]);

  const changeCreate = (key, value) => setCreateForm((f) => ({ ...f, [key]: value }));
  const changeEdit = (key, value) => setEditForm((f) => ({ ...f, [key]: value }));

  const onCreate = async (e) => {
    e.preventDefault();
    try {
      if (!createForm.identifiant?.trim() || !createForm.password?.trim()) {
        throw new Error('Identifiant et mot de passe requis');
      }
      const payload = { ...createForm };
      if (!payload.email?.trim()) delete payload.email;
      await createUser(payload);
      toast.success('Utilisateur créé');
      setCreateOpen(false);
      setCreateForm({
        identifiant: '',
        email: '',
        nom: '',
        prenom: '',
        role: 'secretaire',
        password: '',
        actif: true,
      });
      await refresh();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const startEdit = (u) => {
    setEditing(u.id);
    setEditForm({ ...u, password: '' });
  };
  const cancelEdit = () => {
    setEditing(null);
    setEditForm({});
  };

  const onSave = async () => {
    try {
      const payload = { ...editForm };
      if (!payload.password) delete payload.password;
      if (payload.email !== undefined && payload.email.trim() === '') delete payload.email;
      await updateUser(editing, payload);
      toast.success('Modifications enregistrées');
      cancelEdit();
      await refresh();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const onDelete = async (id) => {
    try {
      await deleteUser(id);
      toast.success('Utilisateur supprimé');
      await refresh();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <PageLayout title="Comptes">
      <div className="px-4 pt-2 pb-24">
        {/* En-tête */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-semibold">Utilisateurs</h1>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition"
            >
              <UserPlus size={16} />
              Nouveau
            </button>
            <button
              onClick={logout}
              className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 transition"
            >
              Déconnexion
            </button>
          </div>
        </div>

        {/* Barre de recherche + filtres */}
        <div className="flex flex-wrap gap-2 mb-3">
          <div className="relative flex-1 min-w-[180px]">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <input
              className="w-full pl-8 pr-8 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
              placeholder="Nom, identifiant, email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="relative">
            <select
              className="appearance-none pl-3 pr-7 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 bg-white"
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
            >
              <option value="">Tous les rôles</option>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
            <ChevronDown
              size={13}
              className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
            />
          </div>
          <div className="relative">
            <select
              className="appearance-none pl-3 pr-7 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 bg-white"
              value={filterActif}
              onChange={(e) => setFilterActif(e.target.value)}
            >
              <option value="">Tous</option>
              <option value="true">Actifs</option>
              <option value="false">Inactifs</option>
            </select>
            <ChevronDown
              size={13}
              className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
            />
          </div>
        </div>

        {/* Compteur résultats */}
        {!loading && !error && (
          <p className="text-xs text-gray-400 mb-3">
            {filtered.length} utilisateur{filtered.length !== 1 ? 's' : ''}
            {filtered.length !== users.length ? ` sur ${users.length}` : ''}
          </p>
        )}

        {/* Liste scrollable */}
        {loading ? (
          <p className="text-sm text-gray-500">Chargement…</p>
        ) : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-sm">
            {users.length === 0
              ? 'Aucun utilisateur'
              : 'Aucun résultat pour cette recherche'}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((u) => (
              <div
                key={u.id}
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
              >
                {editing === u.id ? (
                  /* ---- Formulaire d'édition inline ---- */
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium text-sm">
                        Modifier {u.prenom} {u.nom}
                      </span>
                      <button
                        onClick={cancelEdit}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X size={18} />
                      </button>
                    </div>
                    <UserFormFields form={editForm} onChange={changeEdit} isEdit />
                    <div className="flex justify-end gap-2 mt-4">
                      <button
                        onClick={cancelEdit}
                        className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={onSave}
                        className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700"
                      >
                        Enregistrer
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ---- Ligne normale ---- */
                  <div className="flex items-center gap-3 px-4 py-3">
                    {/* Avatar initiales */}
                    <div
                      className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold ${ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-600'}`}
                    >
                      {(u.prenom?.[0] || '').toUpperCase()}
                      {(u.nom?.[0] || '').toUpperCase()}
                    </div>
                    {/* Infos */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">
                          {u.prenom} {u.nom}
                        </span>
                        <span className="text-xs text-gray-400">({u.identifiant})</span>
                        <span
                          className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${ROLE_COLORS[u.role] || 'bg-gray-100'}`}
                        >
                          {ROLE_LABELS[u.role] || u.role}
                        </span>
                        {!u.actif && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                            Inactif
                          </span>
                        )}
                      </div>
                      {u.email && (
                        <div className="text-xs text-gray-400 truncate mt-0.5">{u.email}</div>
                      )}
                    </div>
                    {/* Actions */}
                    <div className="flex-shrink-0 flex gap-1.5">
                      <button
                        onClick={() => startEdit(u)}
                        className="px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-50 transition"
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() =>
                          setConfirm({ id: u.id, label: `${u.prenom} ${u.nom}` })
                        }
                        className="px-2.5 py-1.5 rounded-lg border border-red-200 text-xs text-red-600 hover:bg-red-50 transition"
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <AdminSettingsPanel />

        {/* Modal création */}
        {createOpen && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setCreateOpen(false)}
            />
            <form
              onSubmit={onCreate}
              className="relative z-10 w-full max-w-lg bg-white rounded-2xl shadow-xl p-5"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Nouvel utilisateur</h2>
                <button
                  type="button"
                  onClick={() => setCreateOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
              <UserFormFields form={createForm} onChange={changeCreate} />
              <div className="flex justify-end gap-2 mt-5">
                <button
                  type="button"
                  onClick={() => setCreateOpen(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700"
                >
                  Créer
                </button>
              </div>
            </form>
          </div>
        )}

        <ConfirmDrawer
          open={!!confirm}
          onClose={() => setConfirm(null)}
          title="Supprimer l'utilisateur ?"
          description={
            confirm ? `Cette action supprimera définitivement ${confirm.label}.` : ''
          }
          confirmLabel="Supprimer"
          onConfirm={() => {
            if (confirm) onDelete(confirm.id);
            setConfirm(null);
          }}
        />
      </div>
    </PageLayout>
  );
}
